import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

import { toast } from 'react-toastify';
import {
  ShieldCheck, Users, Plus, Pencil, Trash2, Eye,
  EyeOff, ChevronRight, Check, X, UserCog, Loader2,
} from 'lucide-react';
import api from '../../../services/api';

const ALL_TABS = [
  { key: 'dashboard',      label: 'Dashboard' },
  { key: 'merchants',      label: 'Merchants' },
  { key: 'users',          label: 'All Users' },
  { key: 'kyc',            label: 'KYC Forms' },
  { key: 'transactions',   label: 'Transactions' },
  { key: 'settlements',    label: 'Settlements' },
  { key: 'webhook_logs',   label: 'Webhook Logs' },
  { key: 'gateway_manage', label: 'Gateway Manage' },
  { key: 'support',        label: 'Support Tickets' },
  { key: 'commission',     label: 'Commission' },
  { key: 'site_settings',  label: 'Site Settings' },
  { key: 'merchant_tabs',  label: 'Merchant Tabs' },
];

const emptyPermissions = () =>
  Object.fromEntries(ALL_TABS.map(t => [t.key, { read: false, write: false }]));

// ── Shared styles ────────────────────────────────────────────────────────────
const C = {
  primary: '#7c3aed', primarySoft: '#f1ebff', primaryTint: '#ddd6fe',
  ink: '#14142b', body: '#4a4a6a', muted: '#6e6e86',
  bg: '#f4f4f9', surface: '#ffffff', border: '#e8e8f1', borderSoft: '#f0f0f6',
  success: '#10b981', successSoft: '#d1fae5',
  danger: '#ef4444', dangerSoft: '#fee2e2',
};

const S = {
  page: { background: 'transparent', padding: '0' },
  header: {
    background: C.surface,
    borderBottom: `1px solid ${C.border}`,
    padding: '20px 28px 0',
  },
  headerTop: {
    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16,
  },
  title:    { fontSize: 20, fontWeight: 700, color: C.ink, margin: 0 },
  subtitle: { fontSize: 13, color: C.muted, margin: '2px 0 0' },
  tabBar:   { display: 'flex', gap: 4, marginTop: 4, paddingBottom: 0 },
  tab: (active) => ({
    padding: '9px 16px',
    fontSize: 13,
    fontWeight: active ? 700 : 600,
    color: active ? C.primary : C.muted,
    background: active ? C.primarySoft : 'transparent',
    borderRadius: 8,
    cursor: 'pointer',
    border: 'none',
    display: 'flex', alignItems: 'center', gap: 6,
    transition: 'all .12s ease',
    userSelect: 'none',
  }),
  body:      { padding: '24px 28px' },
  statsRow:  { display: 'flex', gap: 16, marginBottom: 20 },
  statCard:  () => ({
    flex: 1, background: C.surface, border: `1.5px solid ${C.border}`,
    borderRadius: 14, padding: '18px 20px',
    display: 'flex', flexDirection: 'column', gap: 12,
    boxShadow: '0 1px 4px rgba(20,20,43,.04)',
  }),
  statIcon: (bg) => ({
    width: 38, height: 38, borderRadius: 10, background: bg,
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  }),
  statNum:   { fontSize: 26, fontWeight: 800, color: C.ink, lineHeight: 1, letterSpacing: '-.02em', minHeight: 30, display: 'flex', alignItems: 'center' },
  statLabel: { fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 6 },
  card: {
    background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, overflow: 'hidden',
  },
  cardHeader: {
    padding: '14px 20px', borderBottom: `1px solid ${C.borderSoft}`,
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  },
  cardTitle: { fontSize: 14, fontWeight: 600, color: C.ink },
  btnPrimary: {
    background: C.primary, color: '#fff', border: 'none', borderRadius: 8,
    padding: '8px 16px', fontSize: 13, fontWeight: 700,
    display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer',
  },
  btnGhost: {
    background: C.surface, border: `1px solid ${C.border}`, borderRadius: 7,
    padding: '5px 10px', fontSize: 12, fontWeight: 600, color: C.body,
    cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4,
  },
  btnDanger: {
    background: 'none', border: `1px solid #fecaca`, borderRadius: 7,
    padding: '5px 10px', fontSize: 12, fontWeight: 600, color: C.danger,
    cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4,
  },
  table:   { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: {
    padding: '10px 16px', background: C.bg, color: C.muted, fontWeight: 700,
    fontSize: 11, textTransform: 'uppercase', letterSpacing: '.5px',
    borderBottom: `1px solid ${C.border}`, textAlign: 'left', whiteSpace: 'nowrap',
  },
  td:      { padding: '12px 16px', borderBottom: `1px solid ${C.borderSoft}`, color: C.ink, verticalAlign: 'middle' },
  tdMuted: { padding: '12px 16px', borderBottom: `1px solid ${C.borderSoft}`, color: C.muted, verticalAlign: 'middle' },
  badge: (color, bg) => ({
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, color, background: bg,
  }),
  emptyState: { textAlign: 'center', padding: '48px 20px', color: C.muted, fontSize: 13 },
};

// ── Custom Dialog (no Bootstrap Modal — avoids z-index/backdrop issues) ──────
function Dialog({ show, onHide, size = 'md', children }) {
  useEffect(() => {
    if (show) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [show]);

  if (!show) return null;

  const widths = { sm: 400, md: 520, lg: 720 };

  return createPortal(
    <div
      onClick={onHide}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '16px',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff',
          borderRadius: 14,
          width: '100%',
          maxWidth: widths[size] || 520,
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
        }}
      >
        {children}
      </div>
    </div>,
    document.body
  );
}

// ── Permission toggle pill ───────────────────────────────────────────────────
function TogglePill({ checked, onChange, label }) {
  return (
    <button
      type="button"
      onClick={onChange}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '3px 10px',
        borderRadius: 20,
        fontSize: 12,
        fontWeight: 500,
        cursor: 'pointer',
        border: checked ? '1.5px solid #7c3aed' : '1.5px solid #d1d5db',
        background: checked ? '#f1ebff' : '#f9f9fc',
        color: checked ? '#7c3aed' : '#6e6e86',
        transition: 'all .15s',
      }}
    >
      {checked ? <Check size={11} /> : <X size={11} />}
      {label}
    </button>
  );
}

// ── Role Modal ───────────────────────────────────────────────────────────────
function RoleModal({ show, onHide, role, onSaved }) {
  const isEdit = Boolean(role);
  const [name, setName]         = useState('');
  const [desc, setDesc]         = useState('');
  const [permissions, setPerms] = useState(emptyPermissions());
  const [saving, setSaving]     = useState(false);

  useEffect(() => {
    if (show) {
      if (role) {
        setName(role.name || '');
        setDesc(role.description || '');
        const merged = emptyPermissions();
        if (role.permissions) {
          Object.keys(merged).forEach(k => {
            if (role.permissions[k]) merged[k] = { ...merged[k], ...role.permissions[k] };
          });
        }
        setPerms(merged);
      } else {
        setName(''); setDesc(''); setPerms(emptyPermissions());
      }
    }
  }, [show, role]);

  const toggle = (tab, type) =>
    setPerms(p => ({ ...p, [tab]: { ...p[tab], [type]: !p[tab][type] } }));

  const toggleAll = (type) => {
    const allOn = ALL_TABS.every(t => permissions[t.key][type]);
    setPerms(p => {
      const n = { ...p };
      ALL_TABS.forEach(t => { n[t.key] = { ...n[t.key], [type]: !allOn }; });
      return n;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error('Role name is required');
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/admin/roles/${role.id}`, { name, description: desc, permissions });
        toast.success('Role updated successfully');
      } else {
        await api.post('/admin/roles', { name, description: desc, permissions });
        toast.success('Role created successfully');
      }
      onSaved(); onHide();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save role');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog show={show} onHide={onHide} size="lg">
      <div style={{ borderRadius: 14, overflow: 'hidden' }}>
        <div style={{ padding: '20px 24px 16px', borderBottom: `1px solid ${C.borderSoft}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: C.ink }}>{isEdit ? 'Edit Role' : 'Create Role'}</div>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>Define permissions for each admin panel section</div>
          </div>
          <button onClick={onHide} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, padding: 4 }}>
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ padding: '20px 24px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#4a4a6a', display: 'block', marginBottom: 6 }}>
                  Role Name <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="e.g. Support Agent"
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#4a4a6a', display: 'block', marginBottom: 6 }}>Description</label>
                <input
                  value={desc}
                  onChange={e => setDesc(e.target.value)}
                  placeholder="Optional"
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#14142b' }}>Tab Permissions</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" onClick={() => toggleAll('read')} style={{ ...S.btnGhost, fontSize: 11 }}>
                  Toggle All Read
                </button>
                <button type="button" onClick={() => toggleAll('write')} style={{ ...S.btnGhost, fontSize: 11 }}>
                  Toggle All Write
                </button>
              </div>
            </div>

            <div style={{
              border: '1px solid #e8eaed',
              borderRadius: 8,
              overflow: 'hidden',
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px 120px', background: '#f4f4f9', padding: '8px 16px', fontSize: 11, fontWeight: 600, color: '#6e6e86', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                <div>Section</div>
                <div style={{ textAlign: 'center' }}>Read</div>
                <div style={{ textAlign: 'center' }}>Write</div>
              </div>
              {ALL_TABS.map((t, i) => (
                <div key={t.key} style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 120px 120px',
                  padding: '10px 16px',
                  alignItems: 'center',
                  borderTop: '1px solid #f1f3f4',
                  background: i % 2 === 0 ? '#fff' : '#fafafa',
                }}>
                  <div style={{ fontSize: 13, color: '#4a4a6a', fontWeight: 500 }}>{t.label}</div>
                  <div style={{ textAlign: 'center' }}>
                    <TogglePill
                      checked={permissions[t.key]?.read || false}
                      onChange={() => toggle(t.key, 'read')}
                      label="Read"
                    />
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <TogglePill
                      checked={permissions[t.key]?.write || false}
                      onChange={() => toggle(t.key, 'write')}
                      label="Write"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ padding: '16px 24px', borderTop: '1px solid #f1f3f4', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button type="button" onClick={onHide} style={{ ...S.btnGhost, padding: '8px 16px', fontSize: 13 }}>
              Cancel
            </button>
            <button type="submit" disabled={saving} style={{ ...S.btnPrimary, padding: '8px 18px', opacity: saving ? 0.7 : 1 }}>
              {saving && <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />}
              {isEdit ? 'Save Changes' : 'Create Role'}
            </button>
          </div>
        </form>
      </div>
    </Dialog>
  );
}

// ── Admin Modal ──────────────────────────────────────────────────────────────
function AdminModal({ show, onHide, admin, roles, onSaved }) {
  const isEdit = Boolean(admin);
  const [form, setForm]       = useState({});
  const [showPwd, setShowPwd] = useState(false);
  const [saving, setSaving]   = useState(false);

  useEffect(() => {
    if (show) {
      setForm(admin
        ? { full_name: admin.full_name || '', email: admin.email || '', username: admin.username || '', role_id: admin.role_id || '', is_super_admin: admin.is_super_admin || false, status: admin.status || 'active', password: '' }
        : { full_name: '', email: '', username: '', role_id: '', is_super_admin: false, status: 'active', password: '' }
      );
      setShowPwd(false);
    }
  }, [show, admin]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.username) return toast.error('Fill all required fields');
    if (!isEdit && !form.password) return toast.error('Password is required');
    setSaving(true);
    try {
      const payload = {
        full_name: form.full_name, email: form.email, username: form.username,
        role_id: form.role_id || null, is_super_admin: form.is_super_admin, status: form.status,
      };
      if (form.password) payload.password = form.password;
      if (isEdit) {
        await api.put(`/admin/roles/admins/${admin.id}`, payload);
        toast.success('Admin updated');
      } else {
        await api.post('/admin/roles/admins', payload);
        toast.success('Admin added');
      }
      onSaved(); onHide();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save admin');
    } finally {
      setSaving(false);
    }
  };

  const field = (label, content, required) => (
    <div style={{ marginBottom: 16 }}>
      <label style={{ fontSize: 12, fontWeight: 600, color: '#4a4a6a', display: 'block', marginBottom: 6 }}>
        {label} {required && <span style={{ color: '#ef4444' }}>*</span>}
      </label>
      {content}
    </div>
  );

  const inp = (props) => (
    <input {...props} style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
  );

  return (
    <Dialog show={show} onHide={onHide}>
      <div style={{ borderRadius: 14, overflow: 'hidden' }}>
        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid #f1f3f4', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#14142b' }}>{isEdit ? 'Edit Admin' : 'Add Admin'}</div>
          <button onClick={onHide} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6e6e86' }}><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div style={{ padding: '20px 24px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
              {field('Full Name', inp({ value: form.full_name || '', onChange: e => set('full_name', e.target.value), placeholder: 'Full name' }), true)}
              {field('Username', inp({ value: form.username || '', onChange: e => set('username', e.target.value), placeholder: 'username', disabled: isEdit, style: { width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, outline: 'none', boxSizing: 'border-box', background: isEdit ? '#f9f9fc' : undefined } }), !isEdit)}
            </div>
            {field('Email Address', inp({ type: 'email', value: form.email || '', onChange: e => set('email', e.target.value), placeholder: 'admin@example.com' }), true)}
            {field(isEdit ? 'New Password (leave blank to keep)' : 'Password',
              <div style={{ position: 'relative' }}>
                <input
                  type={showPwd ? 'text' : 'password'}
                  value={form.password || ''}
                  onChange={e => set('password', e.target.value)}
                  placeholder="Minimum 8 characters"
                  style={{ width: '100%', padding: '8px 40px 8px 12px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
                />
                <button type="button" onClick={() => setShowPwd(s => !s)} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#6e6e86', padding: 0 }}>
                  {showPwd ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>,
              !isEdit
            )}
            {field('Assign Role',
              <select
                value={form.role_id || ''}
                onChange={e => set('role_id', e.target.value)}
                disabled={form.is_super_admin}
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, outline: 'none', boxSizing: 'border-box', background: form.is_super_admin ? '#f9f9fc' : '#fff' }}
              >
                <option value="">— No Role —</option>
                {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
              {field('Status',
                <select value={form.status || 'active'} onChange={e => set('status', e.target.value)} style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, outline: 'none', boxSizing: 'border-box' }}>
                  <option value="active">Active</option>
                  <option value="suspended">Suspended</option>
                </select>
              )}
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#4a4a6a', display: 'block', marginBottom: 10 }}>Super Admin</label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.is_super_admin || false} onChange={e => set('is_super_admin', e.target.checked)} style={{ width: 15, height: 15 }} />
                  <span style={{ fontSize: 12, color: '#6e6e86' }}>Full access, ignores role</span>
                </label>
              </div>
            </div>
          </div>
          <div style={{ padding: '14px 24px', borderTop: '1px solid #f1f3f4', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button type="button" onClick={onHide} style={{ ...S.btnGhost, padding: '8px 16px', fontSize: 13 }}>Cancel</button>
            <button type="submit" disabled={saving} style={{ ...S.btnPrimary, padding: '8px 18px', opacity: saving ? 0.7 : 1 }}>
              {saving && <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />}
              {isEdit ? 'Save Changes' : 'Add Admin'}
            </button>
          </div>
        </form>
      </div>
    </Dialog>
  );
}

// ── Permissions View Modal ───────────────────────────────────────────────────
function PermViewModal({ show, onHide, role }) {
  if (!role) return null;
  const perms = role.permissions || {};
  return (
    <Dialog show={show} onHide={onHide} size="md">
      <div style={{ borderRadius: 14, overflow: 'hidden' }}>
        <div style={{ padding: '18px 24px 14px', borderBottom: '1px solid #f1f3f4', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#14142b' }}>{role.name}</div>
            <div style={{ fontSize: 12, color: '#6e6e86', marginTop: 1 }}>{role.description || 'Permission matrix'}</div>
          </div>
          <button onClick={onHide} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6e6e86' }}><X size={18} /></button>
        </div>
        <div style={{ padding: '16px 24px' }}>
          <div style={{ border: '1px solid #e8eaed', borderRadius: 8, overflow: 'hidden' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 80px', background: '#f4f4f9', padding: '8px 16px', fontSize: 11, fontWeight: 600, color: '#6e6e86', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              <div>Section</div><div style={{ textAlign: 'center' }}>Read</div><div style={{ textAlign: 'center' }}>Write</div>
            </div>
            {ALL_TABS.map((t, i) => (
              <div key={t.key} style={{ display: 'grid', gridTemplateColumns: '1fr 80px 80px', padding: '9px 16px', alignItems: 'center', borderTop: '1px solid #f1f3f4', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                <div style={{ fontSize: 13, color: '#4a4a6a' }}>{t.label}</div>
                <div style={{ textAlign: 'center' }}>
                  {perms[t.key]?.read
                    ? <span style={{ color: '#16a34a', display: 'inline-flex' }}><Check size={14} /></span>
                    : <span style={{ color: '#e8e8f1', display: 'inline-flex' }}><X size={14} /></span>}
                </div>
                <div style={{ textAlign: 'center' }}>
                  {perms[t.key]?.write
                    ? <span style={{ color: '#16a34a', display: 'inline-flex' }}><Check size={14} /></span>
                    : <span style={{ color: '#e8e8f1', display: 'inline-flex' }}><X size={14} /></span>}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div style={{ padding: '12px 24px', borderTop: '1px solid #f1f3f4', textAlign: 'right' }}>
          <button onClick={onHide} style={{ ...S.btnGhost, padding: '7px 16px', fontSize: 13 }}>Close</button>
        </div>
      </div>
    </Dialog>
  );
}

// ── Confirm Delete Modal ─────────────────────────────────────────────────────
function ConfirmModal({ show, onHide, title, message, onConfirm, loading }) {
  return (
    <Dialog show={show} onHide={onHide} size="sm">
      <div style={{ borderRadius: 14, overflow: 'hidden', padding: 24 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: '#14142b', marginBottom: 8 }}>{title}</div>
        <div style={{ fontSize: 13, color: '#6e6e86', marginBottom: 20 }}>{message}</div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onHide} style={{ ...S.btnGhost, padding: '7px 14px', fontSize: 13 }}>Cancel</button>
          <button onClick={onConfirm} disabled={loading} style={{ background: '#dc2626', color: '#fff', border: 'none', borderRadius: 7, padding: '7px 14px', fontSize: 13, fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, opacity: loading ? 0.7 : 1 }}>
            {loading ? <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> : null} Delete
          </button>
        </div>
      </div>
    </Dialog>
  );
}

// ── Main ─────────────────────────────────────────────────────────────────────
export default function AdminRoles() {
  const [activeTab, setActiveTab] = useState('roles');
  const [roles, setRoles]         = useState([]);
  const [admins, setAdmins]       = useState([]);
  const [loading, setLoading]     = useState({ roles: true, admins: true });

  const [roleModal, setRoleModal]   = useState(false);
  const [editRole, setEditRole]     = useState(null);
  const [viewRole, setViewRole]     = useState(null);
  const [delRole, setDelRole]       = useState(null);

  const [adminModal, setAdminModal] = useState(false);
  const [editAdmin, setEditAdmin]   = useState(null);
  const [delAdmin, setDelAdmin]     = useState(null);

  const [deleting, setDeleting]     = useState(false);

  const fetchRoles = useCallback(async () => {
    setLoading(l => ({ ...l, roles: true }));
    try { const r = await api.get('/admin/roles'); setRoles(r.data.data || []); }
    catch { toast.error('Failed to load roles'); }
    finally { setLoading(l => ({ ...l, roles: false })); }
  }, []);

  const fetchAdmins = useCallback(async () => {
    setLoading(l => ({ ...l, admins: true }));
    try { const r = await api.get('/admin/roles/admins/list'); setAdmins(r.data.data || []); }
    catch { toast.error('Failed to load admins'); }
    finally { setLoading(l => ({ ...l, admins: false })); }
  }, []);

  useEffect(() => { fetchRoles(); fetchAdmins(); }, [fetchRoles, fetchAdmins]);

  const doDelRole = async () => {
    setDeleting(true);
    try {
      await api.delete(`/admin/roles/${delRole.id}`);
      toast.success('Role deleted'); setDelRole(null);
      fetchRoles(); fetchAdmins();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setDeleting(false); }
  };

  const doDelAdmin = async () => {
    setDeleting(true);
    try {
      await api.delete(`/admin/roles/admins/${delAdmin.id}`);
      toast.success('Admin deleted'); setDelAdmin(null);
      fetchAdmins();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setDeleting(false); }
  };

  const superCount = admins.filter(a => a.is_super_admin).length;

  return (
    <div style={S.page}>
      <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
      {/* Header */}
      <div style={S.header}>
        <div style={S.headerTop}>
          <div>
            <h1 style={S.title}>Roles &amp; Permissions</h1>
            <p style={S.subtitle}>Manage sub-admins and their access to each section of the admin panel</p>
          </div>
        </div>
        {/* Tab bar */}
        <div style={S.tabBar}>
          {[
            { key: 'roles', label: 'Roles', icon: <ShieldCheck size={14} /> },
            { key: 'admins', label: 'Admins', icon: <Users size={14} /> },
          ].map(t => (
            <button key={t.key} style={S.tab(activeTab === t.key)} onClick={() => setActiveTab(t.key)}>
              {t.icon} {t.label}
              <span style={{ background: activeTab === t.key ? '#ddd6fe' : '#f0f0f6', color: activeTab === t.key ? '#7c3aed' : '#6e6e86', fontSize: 11, fontWeight: 600, padding: '1px 7px', borderRadius: 20, marginLeft: 2 }}>
                {t.key === 'roles' ? roles.length : admins.length}
              </span>
            </button>
          ))}
        </div>
      </div>

      <div style={S.body}>
        {/* Stats */}
        <div style={S.statsRow}>
          {[
            { label: 'Total Roles',       value: roles.length,             icon: ShieldCheck, bg: '#f1ebff', color: '#7c3aed' },
            { label: 'Total Admins',      value: admins.length,            icon: Users,       bg: '#f0fdf4', color: '#16a34a' },
            { label: 'Super Admins',      value: superCount,               icon: UserCog,     bg: '#fef3c7', color: '#d97706' },
            { label: 'Role-Based Admins', value: admins.length - superCount, icon: ShieldCheck, bg: '#fdf2f8', color: '#9333ea' },
          ].map(({ label, value, icon: Icon, bg, color }) => (
            <div key={label} style={S.statCard()}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                <div>
                  <div style={S.statLabel}>{label}</div>
                  <div style={S.statNum}>{value}</div>
                </div>
                <div style={S.statIcon(bg)}><Icon size={17} color={color} strokeWidth={1.8} /></div>
              </div>
            </div>
          ))}
        </div>

        {/* ── ROLES PANEL ─────────────────────────────────────────────────── */}
        {activeTab === 'roles' && (
          <div style={S.card}>
            <div style={S.cardHeader}>
              <div style={S.cardTitle}>All Roles</div>
              <button style={S.btnPrimary} onClick={() => { setEditRole(null); setRoleModal(true); }}>
                <Plus size={14} /> Create Role
              </button>
            </div>
            {loading.roles
              ? <div style={S.emptyState}><Loader2 size={24} style={{ animation: 'spin 1s linear infinite', color: C.primary }} /></div>
              : roles.length === 0
                ? (
                  <div style={S.emptyState}>
                    <ShieldCheck size={32} style={{ marginBottom: 8, opacity: 0.3 }} />
                    <div style={{ fontWeight: 500, color: '#4a4a6a', marginBottom: 4 }}>No roles created yet</div>
                    <div>Create a role to define what each sub-admin can access.</div>
                    <button style={{ ...S.btnPrimary, margin: '16px auto 0', display: 'inline-flex' }} onClick={() => { setEditRole(null); setRoleModal(true); }}>
                      <Plus size={14} /> Create First Role
                    </button>
                  </div>
                )
                : (
                  <table style={S.table}>
                    <thead>
                      <tr>
                        {['Role', 'Description', 'Admins Assigned', 'Read Access', 'Write Access', 'Actions'].map(h => (
                          <th key={h} style={S.th}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {roles.map(r => {
                        const perms = r.permissions || {};
                        const readCount  = ALL_TABS.filter(t => perms[t.key]?.read).length;
                        const writeCount = ALL_TABS.filter(t => perms[t.key]?.write).length;
                        return (
                          <tr key={r.id}>
                            <td style={S.td}>
                              <div style={{ fontWeight: 600, color: '#14142b' }}>{r.name}</div>
                            </td>
                            <td style={S.tdMuted}>{r.description || <span style={{ color: '#e8e8f1' }}>—</span>}</td>
                            <td style={S.td}>
                              <span style={S.badge('#4a4a6a', '#f3f4f6')}>{r.admin_users_count ?? 0} admins</span>
                            </td>
                            <td style={S.td}>
                              <span style={S.badge('#15803d', '#f0fdf4')}>{readCount}/{ALL_TABS.length} tabs</span>
                            </td>
                            <td style={S.td}>
                              <span style={S.badge('#6d28d9', '#f1ebff')}>{writeCount}/{ALL_TABS.length} tabs</span>
                            </td>
                            <td style={S.td}>
                              <div style={{ display: 'flex', gap: 6 }}>
                                <button style={S.btnGhost} onClick={() => setViewRole(r)} title="View permissions">
                                  <Eye size={13} /> View
                                </button>
                                <button style={S.btnGhost} onClick={() => { setEditRole(r); setRoleModal(true); }} title="Edit role">
                                  <Pencil size={13} /> Edit
                                </button>
                                <button style={S.btnDanger} onClick={() => setDelRole(r)} title="Delete role">
                                  <Trash2 size={13} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )
            }
          </div>
        )}

        {/* ── ADMINS PANEL ────────────────────────────────────────────────── */}
        {activeTab === 'admins' && (
          <div style={S.card}>
            <div style={S.cardHeader}>
              <div style={S.cardTitle}>All Admins</div>
              <button style={S.btnPrimary} onClick={() => { setEditAdmin(null); setAdminModal(true); }}>
                <Plus size={14} /> Add Admin
              </button>
            </div>
            {loading.admins
              ? <div style={S.emptyState}><Loader2 size={24} style={{ animation: 'spin 1s linear infinite', color: C.primary }} /></div>
              : admins.length === 0
                ? (
                  <div style={S.emptyState}>
                    <Users size={32} style={{ marginBottom: 8, opacity: 0.3 }} />
                    <div style={{ fontWeight: 500, color: '#4a4a6a', marginBottom: 4 }}>No admins added yet</div>
                    <div>Add a sub-admin and assign a role to control their access.</div>
                    <button style={{ ...S.btnPrimary, margin: '16px auto 0', display: 'inline-flex' }} onClick={() => { setEditAdmin(null); setAdminModal(true); }}>
                      <Plus size={14} /> Add First Admin
                    </button>
                  </div>
                )
                : (
                  <table style={S.table}>
                    <thead>
                      <tr>
                        {['Admin', 'Username', 'Email', 'Role', 'Type', 'Status', 'Actions'].map(h => (
                          <th key={h} style={S.th}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {admins.map(a => (
                        <tr key={a.id}>
                          <td style={S.td}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#f1ebff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: '#7c3aed', flexShrink: 0 }}>
                                {(a.full_name || 'A')[0].toUpperCase()}
                              </div>
                              <span style={{ fontWeight: 600, color: '#14142b' }}>{a.full_name}</span>
                            </div>
                          </td>
                          <td style={S.tdMuted}>{a.username}</td>
                          <td style={S.tdMuted}>{a.email}</td>
                          <td style={S.td}>
                            {a.admin_role
                              ? <span style={S.badge('#6d28d9', '#f1ebff')}>{a.admin_role.name}</span>
                              : <span style={{ color: '#e8e8f1', fontSize: 12 }}>—</span>
                            }
                          </td>
                          <td style={S.td}>
                            {a.is_super_admin
                              ? <span style={S.badge('#7c3aed', '#f5f3ff')}>Super Admin</span>
                              : <span style={S.badge('#4a4a6a', '#f3f4f6')}>Role-Based</span>
                            }
                          </td>
                          <td style={S.td}>
                            <span style={a.status === 'active' ? S.badge('#15803d', '#f0fdf4') : S.badge('#b45309', '#fef3c7')}>
                              {a.status === 'active' ? 'Active' : 'Suspended'}
                            </span>
                          </td>
                          <td style={S.td}>
                            <div style={{ display: 'flex', gap: 6 }}>
                              <button style={S.btnGhost} onClick={() => { setEditAdmin(a); setAdminModal(true); }}>
                                <Pencil size={13} /> Edit
                              </button>
                              <button style={S.btnDanger} onClick={() => setDelAdmin(a)}>
                                <Trash2 size={13} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )
            }
          </div>
        )}
      </div>

      {/* Modals */}
      <RoleModal show={roleModal} onHide={() => setRoleModal(false)} role={editRole} onSaved={fetchRoles} />
      <AdminModal show={adminModal} onHide={() => setAdminModal(false)} admin={editAdmin} roles={roles} onSaved={fetchAdmins} />
      <PermViewModal show={Boolean(viewRole)} onHide={() => setViewRole(null)} role={viewRole} />
      <ConfirmModal show={Boolean(delRole)} onHide={() => setDelRole(null)} title="Delete Role"
        message={<>Delete role <strong>{delRole?.name}</strong>? Admins using this role will lose their access settings.</>}
        onConfirm={doDelRole} loading={deleting} />
      <ConfirmModal show={Boolean(delAdmin)} onHide={() => setDelAdmin(null)} title="Delete Admin"
        message={<>Permanently delete <strong>{delAdmin?.full_name}</strong>? This cannot be undone.</>}
        onConfirm={doDelAdmin} loading={deleting} />
    </div>
  );
}

